import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import {
  ActivityIcon,
  PlugZapIcon,
  BookOpenIcon,
  LibraryBigIcon,
  BrainIcon,
  Building2Icon,
  CalendarHeartIcon,
  CalendarIcon,
  ChartColumnIcon,
  ClipboardCheckIcon,
  ClockIcon,
  CoinsIcon,
  ContactRoundIcon,
  FileSignatureIcon,
  BanknoteIcon,
  FileTextIcon,
  BookmarkIcon,
  SearchIcon,
  FlagIcon,
  HomeIcon,
  LandmarkIcon,
  LayersIcon,
  FlaskConicalIcon,
  LayoutGridIcon,
  Link2Icon,
  MapPinIcon,
  MessagesSquareIcon,
  SettingsIcon,
  SlidersHorizontalIcon,
  MegaphoneIcon,
  CircleDollarSignIcon,
  SparklesIcon,
  SparkleIcon,
  ListChecksIcon,
  TruckIcon,
  UserPlusIcon, InboxIcon, FileBarChart2Icon, ReceiptTextIcon, ScaleIcon, WalletIcon, HandCoinsIcon, GaugeIcon, TargetIcon, TrendingUpIcon,
  UsersRoundIcon,
  HeartHandshakeIcon,
  LayoutTemplateIcon,
  MailIcon,
  TagIcon,
} from 'lucide-react';
import { AccountMenu, Shell, cn, type ShellNavItem, type ShellNavSection } from '@bcecloud/portal-ui';
import { NotificationBell } from '@/components/NotificationBell';
import { FeedbackButton } from '@/components/FeedbackButton';
import { PageHelpVideosButton } from '@/components/help/PageHelpVideos';
import { ReleaseAnnouncement } from '@/components/ReleaseAnnouncement';
import { SandboxBanner } from '@/components/SandboxBanner';
import { isSandbox } from '@/sandbox/mode';
import { useQuery } from '@tanstack/react-query';
import { MCP_USAGE_PATH } from '@/lib/mcp-usage';
import { FEATURES } from '@/lib/flags';
import { APP_FEATURE, useAppFeatures, type AppFeatureKey } from '@/lib/app-features';
import {
  avatarSrc,
  hasPermission,
  PERMISSION_MANAGE_CLIENT_PORTALS,
  PERMISSION_VIEW_ANALYTICS,
  PERMISSION_VIEW_ASSISTANT_GAPS,
  PERMISSION_GENERATE_PROPOSALS,
  PERMISSION_WORK_REPEAT_CLIENTS,
  PERMISSION_VIEW_DEV_TRACKER,
  PERMISSION_VIEW_SALES_DASHBOARD,
  PERMISSION_VIEW_ACCOUNTING,
  PERMISSION_MANAGE_CONTRACTS,
  PERMISSION_MANAGE_PAYMENTS,
  PERMISSION_PROCESS_INVOICES,
  readMyIdentity,
} from '@/lib/events';
import { onAssistantOpenRequest } from '@/components/assistant/assistant-store';
import { LEGACY_PORTAL_LABEL, LEGACY_PORTAL_URL } from '@/lib/launch';
import { useReportAppVisit } from '@/lib/use-report-app-visit';
import { ACCOUNTING_PATH } from '@/lib/finance';
import { INVOICES_PATH } from '@/lib/invoices';
import { ChartPieIcon, RepeatIcon, UserRoundCheckIcon } from 'lucide-react';
import { REPEAT_CLIENTS_PATH } from '@/lib/repeat-clients';
import { OUTREACH_TEMPLATES_PATH } from '@/lib/outreach-templates';
import { LEAD_PANEL_PATH } from '@/pages/LeadPanelPage';
import { PROGRAMS_PATH } from '@/lib/program-profitability';
import { FACILITATORS_PATH } from '@/lib/facilitator-spend';

/**
 * Lazy on purpose: the assistant drags in streaming-markdown rendering (and
 * its syntax highlighter), which would triple the main bundle for a panel
 * most builds never show. Nothing is fetched until the panel first opens.
 */
const AssistantPanel = lazy(() =>
  import('./assistant/AssistantPanel').then((module) => ({ default: module.AssistantPanel })),
);

/**
 * Top-level sections. Kept as data so the nav and routes cannot drift apart.
 *
 * Events lead: this tool is becoming the main BCEcloud admin, and the everyday
 * work is operating events. Administration groups what changes the platform
 * itself — client portals today; settings and reporting as they arrive.
 */
/** Nav items that belong to a switchable feature (Settings → Features). */
const FEATURE_NAV: Readonly<Record<string, AppFeatureKey>> = {
  '/proposals': APP_FEATURE.proposals,
  '/page-library': APP_FEATURE.pageLibrary,
  '/templates': APP_FEATURE.noteTemplates,
};

/** Base nav; the Gaps item is appended per-identity (developer flag). */
const SECTIONS: readonly ShellNavSection[] = [
  {
    label: null,
    items: [
      { to: '/events', label: 'Events', icon: <CalendarIcon /> },
      { to: '/shipments', label: 'Shipments', icon: <TruckIcon /> },
      { to: '/recaps', label: 'Event recaps', icon: <ClipboardCheckIcon /> },
    ],
  },
  {
    label: 'Catalog',
        defaultCollapsed: true,
    items: [
      { to: '/programs', label: 'Programs', icon: <LayoutGridIcon /> },
      { to: '/season-tags', label: 'Season tags', icon: <CalendarHeartIcon /> },
      // Roy's "Perfect pairings": curated beside the programs they pair.
      { to: '/program-pairings', label: 'Pairings', icon: <Link2Icon /> },
    ],
  },
  {
    label: 'People',
        defaultCollapsed: true,
    items: [
      { to: '/people', label: 'Directory', icon: <ContactRoundIcon /> },
      { to: '/staff-contacts', label: 'Staff contacts', icon: <UsersRoundIcon /> },
      { to: '/charities', label: 'Charities', icon: <HeartHandshakeIcon /> },
    ],
  },
  {
    label: 'Administration',
        defaultCollapsed: true,
    items: [
      { to: '/organizations', label: 'Organizations', icon: <Building2Icon /> },
      // The client portals' Resource center (Roy's portal list, item 13).
      { to: '/resources', label: 'Resources', icon: <LibraryBigIcon /> },
      { to: '/pricing-templates', label: 'Pricing templates', icon: <CoinsIcon /> },
      { to: '/portal-defaults', label: 'Portal defaults', icon: <SlidersHorizontalIcon /> },
      { to: '/promo-banners', label: 'Overview banners', icon: <MegaphoneIcon /> },
    ],
  },
  ...(FEATURES.assistant
    ? [
        {
          label: 'AI Assistant',
        defaultCollapsed: true,
          items: [
            { to: '/assistant/skills', label: 'Skills', icon: <SparklesIcon /> },
            { to: '/assistant/memory', label: 'Memory', icon: <BrainIcon /> },
            { to: '/assistant/chats', label: 'Chats', icon: <MessagesSquareIcon /> },
            { to: '/assistant/schedules', label: 'Schedules', icon: <ClockIcon /> },
          ],
        } satisfies ShellNavSection,
      ]
    : []),
];

export interface AdminShellProps {
  readonly children: React.ReactNode;
}

/** The Analytics section's items for the view_analytics grant (Sales has its own). */
const ANALYTICS_ITEMS: readonly ShellNavItem[] = [
  { to: '/leads', label: 'Leads', icon: <UserPlusIcon /> },
  { to: '/submissions', label: 'Submissions', icon: <InboxIcon /> },
  { to: '/analytics/events', label: 'Events', icon: <ChartColumnIcon /> },
  { to: '/analytics/pages', label: 'Pages', icon: <FileTextIcon /> },
  { to: '/analytics/page-types', label: 'Page types', icon: <LayersIcon /> },
  { to: '/analytics/page-tests', label: 'Page tests', icon: <FlaskConicalIcon /> },
  { to: '/analytics/keywords', label: 'Keywords', icon: <SearchIcon /> },
  { to: '/analytics/ppc', label: 'PPC', icon: <CircleDollarSignIcon /> },
  { to: '/analytics/pricing', label: 'Pricing', icon: <TagIcon /> },
  { to: '/analytics/targeted', label: 'Targeted analytics', icon: <TargetIcon /> },
  { to: MCP_USAGE_PATH, label: 'MCP usage', icon: <PlugZapIcon /> },
];

/**
 * The Company Analyst reports: listed under Analytics, but every report
 * carries a QuickBooks spend section, so it follows view_accounting.
 */
const REPORTS_ITEM: ShellNavItem = { to: '/reports', label: 'Reports', icon: <FileBarChart2Icon /> };

/**
 * Analytics → Accounting, behind view_accounting: the 13-week cash forecast
 * (cash flow, receivables, assumptions, forecast vs actual), then the books.
 */
const ACCOUNTING_ITEMS: readonly ShellNavItem[] = [
  { to: ACCOUNTING_PATH.cashFlow, label: 'Cash flow', icon: <WalletIcon /> },
  { to: ACCOUNTING_PATH.receivables, label: 'Receivables', icon: <HandCoinsIcon /> },
  { to: ACCOUNTING_PATH.assumptions, label: 'Assumptions', icon: <SlidersHorizontalIcon /> },
  { to: ACCOUNTING_PATH.forecastAccuracy, label: 'Forecast vs actual', icon: <GaugeIcon /> },
  { to: ACCOUNTING_PATH.expenses, label: 'Expenses', icon: <ReceiptTextIcon /> },
  { to: ACCOUNTING_PATH.pnl, label: 'Profit & Loss', icon: <ScaleIcon /> },
  { to: PROGRAMS_PATH, label: 'Programs', icon: <ChartPieIcon /> },
  { to: FACILITATORS_PATH.list, label: 'Facilitators', icon: <UsersRoundIcon /> },
];

/**
 * BCE-branded chrome for the admin tool — the same shell as the client portal,
 * in BCE's own colors.
 *
 * Sharing the layout is deliberate: staff move between this tool and client
 * portals all day, and identical chrome means nothing has to be relearned.
 * What is equally deliberate is that this shell **never re-themes**: client
 * branding is content here, shown in previews and swatches, and a shell that
 * changed color per client would make it easy to act on the wrong one.
 *
 * @param props - The page to render inside the shell.
 * @returns The application shell.
 */
export function AdminShell({ children }: AdminShellProps): React.ReactElement {
  const clerk = useClerk();
  const { user } = useUser();
  const navigate = useNavigate();
  // The proposal editor is a full-height app; it fills the content column
  // (narrowing beside the assistant) instead of scrolling as a padded page.
  const fillContent = /^\/proposals\/[a-f0-9]{24}/i.test(useLocation().pathname);
  const [assistantOpen, setAssistantOpen] = useState(false);

  // Adoption: one ping per signed-in session, from the shell because it is the
  // one component every authenticated page renders inside.
  useReportAppVisit();

  // The Gaps page is developer-flag-gated on the server; the nav item simply
  // hides for everyone else.
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
  const isFeatureOn = useAppFeatures();
  const sections = useMemo<readonly ShellNavSection[]>(() => {
    // The Administration surface is gated per item: the client-portal pages
    // (organizations, pricing templates, portal defaults) need the
    // manage-portals permission, Integrations needs the admin role. The
    // server enforces both; hiding just avoids dead links.
    const canManagePortals = hasPermission(identity.data, PERMISSION_MANAGE_CLIENT_PORTALS);
    const isAdmin = ['admin', 'superadmin'].includes(identity.data?.role ?? '');
    let sections = SECTIONS.map((section) =>
      section.label === 'Administration'
        ? {
            ...section,
            items: [
              ...(canManagePortals ? section.items : []),
              // Soft-launch adoption — admin-only, and removed with the launch.
              ...(isAdmin ? [{ to: '/adoption', label: 'Adoption', icon: <ActivityIcon /> }] : []),
              ...(isAdmin ? [{ to: '/templates', label: 'Note templates', icon: <LayoutTemplateIcon /> }] : []),
              ...(isAdmin ? [{ to: '/settings', label: 'Settings', icon: <SettingsIcon /> }] : []),
            ],
          }
        : section,
    ).filter((section) => section.items.length > 0);
    // Directory surfaces (client contacts, accounts, places) are internal-
    // only — the routes 403 everyone else; hiding just avoids dead links.
    const isInternal = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');
    if (!isInternal) {
      // Contractors keep their own events and recaps. Shipments, the program
      // catalog, the people directory and the assistant pages are staff-wide
      // surfaces — the API 403s non-internal roles; hiding just avoids dead
      // links. Program context reaches contractors through the assistant on
      // their own events, not a catalog to browse.
      const staffOnlySections = new Set(['People', 'AI Assistant', 'Catalog']);
      sections = sections
        .filter((section) => !staffOnlySections.has(section.label ?? ''))
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => item.to !== '/shipments'),
        }))
        .filter((section) => section.items.length > 0);
    }
    // Analytics follows Administration. Permission-gated (superadmins hold
    // every grant implicitly): lead volume is management reporting and the
    // rows are prospect PII — the API 403s everyone else; hiding just
    // avoids a dead link.
    // Sales (the sales dashboard) follows its own grant, view_sales_dashboard:
    // management only (hq sales D7), so it can be held without the rest.
    // Accounting (expenses, P&L, and the reports that carry spend) follows
    // view_accounting: owner-level financials, named people only. It is its
    // own group directly under Analytics.
    const canViewAnalytics = hasPermission(identity.data, PERMISSION_VIEW_ANALYTICS);
    const canViewSales = hasPermission(identity.data, PERMISSION_VIEW_SALES_DASHBOARD);
    const canViewAccounting = hasPermission(identity.data, PERMISSION_VIEW_ACCOUNTING);
    const analyticsGroups: ShellNavSection[] = [
      {
        label: 'Analytics',
        defaultCollapsed: true,
        items: [
          // Sales merged the Bookings page in, so either grant shows it.
          ...(canViewSales || canViewAnalytics ? [{ to: '/analytics/sales', label: 'Sales', icon: <TrendingUpIcon /> }] : []),
          ...(canViewAnalytics ? ANALYTICS_ITEMS : []),
          ...(canViewAccounting ? [REPORTS_ITEM] : []),
        ],
      },
      {
        label: 'Accounting',
        defaultCollapsed: true,
        items: canViewAccounting ? ACCOUNTING_ITEMS : [],
      },
    ].filter((section) => section.items.length > 0);
    if (analyticsGroups.length > 0) {
      const adminIndex = sections.findIndex((section) => section.label === 'Administration');
      sections =
        adminIndex === -1
          ? [...sections, ...analyticsGroups]
          : [...sections.slice(0, adminIndex + 1), ...analyticsGroups, ...sections.slice(adminIndex + 1)];
    }
    // Sales — proposals and contracts, its own section so it can grow
    // (reach-out next). Each item follows its own grant (generate_proposals,
    // manage_contracts; superadmins hold both implicitly), so the section
    // shows only what someone may open. Sits right after operations, high in
    // the nav for the salespeople who live in it.
    const salesItems = [
      // Repeat clients: its own grant, so it ships while My day stays dark.
      ...(hasPermission(identity.data, PERMISSION_WORK_REPEAT_CLIENTS)
        ? [{ to: REPEAT_CLIENTS_PATH, label: 'Repeat clients', icon: <RepeatIcon /> }]
        : []),
      // Each rep's outreach templates for Repeat clients, on the same grant.
      ...(hasPermission(identity.data, PERMISSION_WORK_REPEAT_CLIENTS)
        ? [{ to: OUTREACH_TEMPLATES_PATH, label: 'My email templates', icon: <MailIcon /> }]
        : []),
      // The Lead Control Panel: every staff member sees the queue and their leads.
      ...(isInternal ? [{ to: LEAD_PANEL_PATH, label: 'Lead Control Panel', icon: <UserRoundCheckIcon /> }] : []),
      ...(hasPermission(identity.data, PERMISSION_GENERATE_PROPOSALS)
        ? [{ to: '/proposals', label: 'Proposals', icon: <FileTextIcon /> }]
        : []),
      // The library lives inside Proposals: it goes when Proposals is switched off too.
      ...(hasPermission(identity.data, PERMISSION_GENERATE_PROPOSALS) && isFeatureOn(APP_FEATURE.proposals)
        ? [{ to: '/page-library', label: 'Page library', icon: <BookmarkIcon /> }]
        : []),
      ...(hasPermission(identity.data, PERMISSION_MANAGE_CONTRACTS)
        ? [{ to: '/contracts', label: 'Contracts', icon: <FileSignatureIcon /> }]
        : []),
      ...(hasPermission(identity.data, PERMISSION_MANAGE_PAYMENTS)
        ? [{ to: '/payments', label: 'Payments', icon: <BanknoteIcon /> }]
        : []),
    ];
    if (salesItems.length > 0) {
      const sales: ShellNavSection = { label: 'Sales', items: salesItems };
      sections = [sections[0], sales, ...sections.slice(1)].filter(
        (section): section is ShellNavSection => section !== undefined,
      );
    }
    // Tools: work that turns documents into records (invoices into expense
    // lines first). Each item follows its own grant; the section sits right
    // after Sales.
    const toolItems: ShellNavItem[] = [
      ...(hasPermission(identity.data, PERMISSION_PROCESS_INVOICES)
        ? [{ to: INVOICES_PATH, label: 'Invoices', icon: <ReceiptTextIcon /> }]
        : []),
    ];
    if (toolItems.length > 0) {
      const tools: ShellNavSection = { label: 'Tools', items: toolItems };
      const salesIndex = sections.findIndex((section) => section.label === 'Sales');
      const at = salesIndex === -1 ? 1 : salesIndex + 1;
      sections = [...sections.slice(0, at), tools, ...sections.slice(at)];
    }
    if (isInternal) {
      sections = sections.map((section) =>
        section.label === 'People'
          ? {
              ...section,
              items: [...section.items, { to: '/accounts', label: 'Accounts', icon: <LandmarkIcon /> }],
            }
          : section,
      );
      const adminIndex = sections.findIndex((section) => section.label === 'Administration');
      const places = {
        label: 'Places',
        defaultCollapsed: true,
        items: [
          { to: '/cities', label: 'Cities', icon: <MapPinIcon /> },
          { to: '/venues', label: 'Venues', icon: <HomeIcon /> },
        ],
      };
      sections =
        adminIndex === -1
          ? [...sections, places]
          : [...sections.slice(0, adminIndex), places, ...sections.slice(adminIndex)];
    }
    const canViewGaps =
      identity.data?.developer === true ||
      hasPermission(identity.data, PERMISSION_VIEW_ASSISTANT_GAPS);
    if (FEATURES.assistant && canViewGaps) {
      sections = sections.map((section) =>
        section.label === 'AI Assistant'
          ? { ...section, items: [...section.items, { to: '/assistant/gaps', label: 'Gaps', icon: <FlagIcon /> }] }
          : section,
      );
    }
    // Help closes the list for everyone: the documentation and what changed.
    sections = [
      ...sections,
      {
        label: 'Help',
        defaultCollapsed: true,
        items: [
          { to: '/help', label: 'Help', icon: <BookOpenIcon /> },
          { to: '/whats-new', label: "What's new", icon: <SparkleIcon /> },
          // Named people only (the view_dev_tracker grant); the API enforces the same.
          ...(hasPermission(identity.data, PERMISSION_VIEW_DEV_TRACKER)
            ? [{ to: '/development', label: 'Development tracker', icon: <ListChecksIcon /> }]
            : []),
        ],
      },
    ];
    // Last: drop what an admin has switched off, and any section left empty.
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          const feature = FEATURE_NAV[item.to];
          return feature === undefined || isFeatureOn(feature);
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [identity.data, isFeatureOn]);

  // Pages (e.g. Chats → "Continue this chat") ask the shell to open the
  // panel through the assistant store. Close-then-open on purpose: the panel
  // restores the conversation from the store ON MOUNT, so a remount is what
  // makes "continue" work when the panel is already open on another chat.
  useEffect(() => {
    onAssistantOpenRequest(() => {
      setAssistantOpen(false);
      window.setTimeout(() => setAssistantOpen(true), 0);
    });
    return () => onAssistantOpenRequest(null);
  }, []);

  const name = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? 'BCE staff';
  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  // Clerk always serves an image URL, generating a placeholder when nothing
  // was uploaded — hasImage is what distinguishes a real photo. Without one,
  // fall back to the BCEcloud profile picture.
  const avatarUrl = user?.hasImage
    ? user.imageUrl
    : avatarSrc({ _id: identity.data?.id, avatar: identity.data?.avatar });
  const myId = identity.data?.id;

  return (
    <Shell
      // No sidebarColor: the Shell falls back to --portal-brand-sidebar, which
      // bce-theme.css pins to BCE navy. Passing a hex here would fork the brand.
      identity={{ name: 'BCE', tagline: 'PORTAL ADMIN' }}
      sections={sections}
      fillContent={fillContent}
      banner={isSandbox() ? <SandboxBanner /> : undefined}
      topbarRight={
        <span className="flex items-center gap-2">
          {/* Shows only on screens a help video covers. */}
          <PageHelpVideosButton />
          <FeedbackButton />
          <NotificationBell />
          {FEATURES.assistant ? (
          <button
            type="button"
            aria-pressed={assistantOpen}
            onClick={() => setAssistantOpen((open) => !open)}
            className={cn(
              'flex h-9 shrink-0 items-center gap-1.5 rounded-portal-pill border px-3 text-portal-micro font-bold whitespace-nowrap transition-colors',
              assistantOpen
                ? 'border-brand bg-portal-sky text-brand'
                : 'border-portal-line text-portal-muted hover:border-brand hover:text-brand',
            )}
          >
            <SparklesIcon aria-hidden="true" className="size-3.5" />
            Assistant
          </button>
          ) : null}
        </span>
      }
      aside={
        FEATURES.assistant && assistantOpen ? (
          <Suspense fallback={null}>
            <AssistantPanel onClose={() => setAssistantOpen(false)} />
          </Suspense>
        ) : undefined
      }
      account={(variant) => (
        <AccountMenu
          name={name}
          email={email}
          avatarUrl={avatarUrl}
          standing="BCE team"
          badge="Portal admin"
          actions={[
            // The in-app profile page, not Clerk's modal — edits there sync
            // back to Clerk (names, photo) through the user-sync pipeline.
            ...(myId === undefined
              ? []
              : [{ label: 'Your profile', onSelect: () => navigate(`/people/${myId}`) }]),
            // The way back to the classic portal, permanently available.
            {
              label: LEGACY_PORTAL_LABEL,
              onSelect: () => window.location.assign(LEGACY_PORTAL_URL),
            },
            { label: 'Sign out', onSelect: () => void clerk.signOut(), tone: 'danger' },
          ]}
          variant={variant}
        />
      )}
    >
      <ReleaseAnnouncement />
      {children}
    </Shell>
  );
}
